import { useTheme } from '@fti/design-system';
import { useSelectableBillingCodes } from '../../../api/media-sim/billingCodes';

/** The API validates billing_code against this exact shape. */
export const BILLING_CODE_REGEX = /^\d{6}\.\d{4}-\d{3}$/;

interface BillingCodeFieldProps {
  value: string;
  onChange: (code: string) => void;
  /** Validation message to show under the field, if any. */
  error?: string | null;
  disabled?: boolean;
}

/**
 * Billing code entry: a text field for client codes, plus a picker for the
 * practice development and new business codes.
 *
 * Client codes are typed because there is no register of them, so only their
 * format can be checked. The non-billable codes come from the API, so picking
 * one is exact and avoids transcribing digits by hand. Selecting from the
 * picker writes only the code into the field; the label is never stored,
 * because the API would reject a value with text appended.
 *
 * Used both at project creation and when editing a registered project, since
 * the point is to start with a practice code and swap in the client code once
 * accounting has raised it.
 */
export function BillingCodeField({
  value,
  onChange,
  error,
  disabled = false,
}: BillingCodeFieldProps) {
  const { theme } = useTheme();
  const { data: codes } = useSelectableBillingCodes();

  // Group by category so Practice Development and New Business are distinct,
  // which is how usage gets reported on.
  const grouped = (codes ?? []).reduce<Record<string, typeof codes>>(
    (acc, entry) => {
      acc[entry.category] = [...(acc[entry.category] ?? []), entry];
      return acc;
    },
    {}
  );

  const selectedPreset = (codes ?? []).find((c) => c.code === value);

  const inputStyle = {
    width: '100%',
    padding: '8px 10px',
    fontSize: '0.9rem',
    borderRadius: '4px',
    border: `1px solid ${
      error ? theme.colors.semantic.error.default : theme.colors.border.default
    }`,
    background: disabled ? theme.colors.background.elevated : undefined,
    color: theme.colors.text.primary,
  };

  return (
    <div>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="005110.0057-100"
        disabled={disabled}
        aria-label="Billing code"
        style={inputStyle}
      />

      {codes && codes.length > 0 && (
        <div style={{ marginTop: '6px' }}>
          <label
            style={{
              fontSize: '0.75rem',
              color: theme.colors.text.secondary,
              display: 'block',
              marginBottom: '2px',
            }}
          >
            No client code yet? Use a practice or new business code
          </label>
          <select
            value={selectedPreset ? selectedPreset.code : ''}
            onChange={(e) => {
              if (e.target.value) onChange(e.target.value);
            }}
            disabled={disabled}
            aria-label="Select a practice or new business code"
            style={{ ...inputStyle, marginTop: 0 }}
          >
            <option value="">Select a code...</option>
            {Object.entries(grouped).map(([category, entries]) => (
              <optgroup key={category} label={category}>
                {(entries ?? []).map((entry) => (
                  <option key={entry.code} value={entry.code}>
                    {entry.label} — {entry.code}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
      )}

      {error ? (
        <p
          style={{
            fontSize: '0.75rem',
            color: theme.colors.semantic.error.default,
            marginTop: '4px',
          }}
        >
          {error}
        </p>
      ) : (
        <p
          style={{
            fontSize: '0.75rem',
            color: theme.colors.text.secondary,
            marginTop: '4px',
          }}
        >
          {selectedPreset
            ? selectedPreset.label
            : 'Format: NNNNNN.NNNN-NNN'}
        </p>
      )}
    </div>
  );
}
